import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { SellerDetailsModal } from '@/components/admin/seller-details-modal'
import { Toast } from '@/components/ui/toast'
import { BackButton } from '@/components/ui/back-button'
import {
  Search,
  Filter,
  ArrowLeft,
  Store,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Crown,
  Badge,
  Shield
} from 'lucide-react'

export function AdminSellersPage() {
  const navigate = useNavigate()
  const [sellers, setSellers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSeller, setSelectedSeller] = useState<any>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [showPremiumModal, setShowPremiumModal] = useState(false)
  const [showVerifyModal, setShowVerifyModal] = useState(false)

  useEffect(() => {
    fetchSellers()
  }, [filter, searchQuery])

  const fetchSellers = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('seller_stores')
        .select(`
          *,
          profiles:seller_id (
            id,
            full_name,
            email,
            phone_number,
            avatar_url,
            is_verified
          )
        `)
        .order('created_at', { ascending: false })

      if (filter !== 'all') {
        query = query.eq('status', filter)
      }

      if (searchQuery) {
        query = query.or(`name.ilike.%${searchQuery}%,applicant_name.ilike.%${searchQuery}%,applicant_email.ilike.%${searchQuery}%`)
      }

      const { data, error } = await query

      if (error) throw error
      setSellers(data || [])
    } catch (err) {
      console.error('Error fetching sellers:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (action: 'approve' | 'reject' | 'block' | 'unblock') => {
    if (!selectedSeller) return

    try {
      setLoading(true)
      const status = action === 'approve' ? 'approved' : 
                    action === 'reject' ? 'rejected' :
                    action === 'block' ? 'suspended' : 'approved'

      const { error: updateError } = await supabase
        .from('seller_stores')
        .update({ status })
        .eq('id', selectedSeller.id)

      if (updateError) throw updateError

      // Update seller profile status
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          status: status === 'approved' ? 'active' : status === 'rejected' ? 'blocked' : status,
          role: status === 'approved' ? 'seller' : 'buyer'
        })
        .eq('id', selectedSeller.seller_id)

      if (profileError) throw profileError

      // Create notification
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([{
          user_id: selectedSeller.seller_id,
          title: `Demande Vendeur ${status === 'approved' ? 'Approuvée' : status === 'rejected' ? 'Refusée' : status === 'suspended' ? 'Suspendue' : 'Réactivée'}`,
          content: status === 'approved' 
            ? 'Votre demande vendeur a été approuvée ! Vous pouvez maintenant commencer à vendre.'
            : `Votre demande vendeur a été ${status === 'rejected' ? 'refusée' : status === 'suspended' ? 'suspendue' : 'réactivée'}.`,
          type: 'system'
        }])

      if (notificationError) throw notificationError

      setToastMessage(`Vendeur ${status === 'approved' ? 'approuvé' : status === 'rejected' ? 'refusé' : status === 'suspended' ? 'suspendu' : 'réactivé'} avec succès`)
      setToastType('success')
      setShowToast(true)
      setShowDetails(false)
      fetchSellers()
    } catch (err) {
      console.error('Error updating seller:', err)
      setToastMessage('Une erreur est survenue')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePremium = async () => {
    if (!selectedSeller) return

    try {
      setLoading(true)
      
      // Toggle the subscription type
      const newSubscriptionType = selectedSeller.subscription_type === 'premium' ? 'free' : 'premium'
      
      const { error: updateError } = await supabase
        .from('seller_stores')
        .update({ 
          subscription_type: newSubscriptionType,
          // Reset product count if downgrading from premium to free
          monthly_product_count: newSubscriptionType === 'free' ? 0 : selectedSeller.monthly_product_count
        })
        .eq('id', selectedSeller.id)

      if (updateError) throw updateError

      // Create notification
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([{
          user_id: selectedSeller.seller_id,
          title: `Abonnement ${newSubscriptionType === 'premium' ? 'Premium' : 'Gratuit'}`,
          content: newSubscriptionType === 'premium' 
            ? 'Votre boutique a été mise à niveau vers l\'abonnement Premium par l\'administrateur.'
            : 'Votre boutique a été rétrogradée vers l\'abonnement Gratuit par l\'administrateur.',
          type: 'system'
        }])

      if (notificationError) throw notificationError

      setToastMessage(`Abonnement du vendeur mis à jour avec succès (${newSubscriptionType === 'premium' ? 'Premium' : 'Gratuit'})`)
      setToastType('success')
      setShowToast(true)
      setShowPremiumModal(false)
      fetchSellers()
    } catch (err) {
      console.error('Error updating seller subscription:', err)
      setToastMessage('Une erreur est survenue lors de la mise à jour de l\'abonnement')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleVerification = async () => {
    if (!selectedSeller) return

    try {
      setLoading(true)
      
      // Get current verification status
      const isCurrentlyVerified = selectedSeller.profiles?.is_verified || false
      
      // Update the verification status
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          is_verified: !isCurrentlyVerified
        })
        .eq('id', selectedSeller.seller_id)

      if (updateError) throw updateError

      // Create notification
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([{
          user_id: selectedSeller.seller_id,
          title: `Compte ${!isCurrentlyVerified ? 'Vérifié' : 'Non Vérifié'}`,
          content: !isCurrentlyVerified 
            ? 'Félicitations ! Votre compte vendeur a été vérifié par l\'administrateur. Un badge de vérification apparaîtra maintenant sur votre profil.'
            : 'Votre badge de vérification a été retiré par l\'administrateur.',
          type: 'system'
        }])

      if (notificationError) throw notificationError

      setToastMessage(`Statut de vérification mis à jour avec succès (${!isCurrentlyVerified ? 'Vérifié' : 'Non Vérifié'})`)
      setToastType('success')
      setShowToast(true)
      setShowVerifyModal(false)
      fetchSellers()
    } catch (err) {
      console.error('Error updating seller verification:', err)
      setToastMessage('Une erreur est survenue lors de la mise à jour du statut de vérification')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {showDetails && selectedSeller && (
        <SellerDetailsModal
          store={selectedSeller}
          onClose={() => {
            setShowDetails(false)
            setSelectedSeller(null)
          }}
          onAction={handleAction}
        />
      )}

      {/* Premium Subscription Modal */}
      {showPremiumModal && selectedSeller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mx-auto">
              <Crown className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedSeller.subscription_type === 'premium' 
                  ? 'Rétrograder vers l\'abonnement Gratuit' 
                  : 'Mettre à niveau vers Premium'}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {selectedSeller.subscription_type === 'premium'
                    ? `Êtes-vous sûr de vouloir rétrograder la boutique "${selectedSeller.name}" vers l'abonnement Gratuit ? Le vendeur sera limité à 8 produits par mois.`
                    : `Êtes-vous sûr de vouloir mettre à niveau la boutique "${selectedSeller.name}" vers l'abonnement Premium ? Le vendeur pourra publier jusqu'à 25 produits par mois.`}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowPremiumModal(false)}
              >
                Annuler
              </Button>
              <Button
                variant={selectedSeller.subscription_type === 'premium' ? 'outline' : 'primary'}
                className={selectedSeller.subscription_type === 'premium' ? 'text-red-600 hover:text-red-700' : ''}
                onClick={handleTogglePremium}
                disabled={loading}
              >
                {loading ? 'Traitement...' : (
                  selectedSeller.subscription_type === 'premium' 
                    ? 'Rétrograder' 
                    : 'Mettre à niveau'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerifyModal && selectedSeller && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full m-4">
            <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mx-auto">
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedSeller.profiles?.is_verified 
                  ? 'Retirer la vérification' 
                  : 'Vérifier ce vendeur'}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  {selectedSeller.profiles?.is_verified
                    ? `Êtes-vous sûr de vouloir retirer le badge de vérification de "${selectedSeller.name}" ? Cela pourrait affecter la confiance des acheteurs.`
                    : `Êtes-vous sûr de vouloir vérifier la boutique "${selectedSeller.name}" ? Un badge de vérification sera affiché sur son profil et ses produits.`}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowVerifyModal(false)}
              >
                Annuler
              </Button>
              <Button
                variant={selectedSeller.profiles?.is_verified ? 'outline' : 'primary'}
                className={selectedSeller.profiles?.is_verified ? 'text-red-600 hover:text-red-700' : ''}
                onClick={handleToggleVerification}
                disabled={loading}
              >
                {loading ? 'Traitement...' : (
                  selectedSeller.profiles?.is_verified 
                    ? 'Retirer la vérification' 
                    : 'Vérifier ce vendeur'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <BackButton to="/admin" label="Retour" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestion des Vendeurs
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez les demandes et les comptes vendeurs
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un vendeur..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as typeof filter)}
                  className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="approved">Approuvés</option>
                  <option value="rejected">Refusés</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : sellers.length === 0 ? (
              <div className="text-center py-12">
                <Store className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun vendeur</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucun vendeur ne correspond à vos critères
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Vendeur
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Boutique
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Abonnement
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sellers.map((seller) => (
                      <tr key={seller.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              {seller.profiles?.avatar_url ? (
                                <img
                                  src={seller.profiles.avatar_url}
                                  alt={seller.applicant_name}
                                  className="h-10 w-10 rounded-full"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <User className="h-5 w-5 text-gray-500" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="flex items-center">
                                <div className="text-sm font-medium text-gray-900">
                                  {seller.applicant_name}
                                </div>
                                {seller.profiles?.is_verified && (
                                  <Badge className="h-4 w-4 ml-1 text-blue-500" />
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {seller.applicant_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{seller.name}</div>
                          <div className="text-sm text-gray-500">
                            {seller.categories?.join(', ')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{seller.applicant_phone}</div>
                          <div className="text-sm text-gray-500">{seller.address}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(seller.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {seller.subscription_type === 'premium' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <Crown className="h-3 w-3 mr-1" />
                                Premium
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Gratuit
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            seller.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : seller.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : seller.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {seller.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                            {seller.status === 'approved' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {seller.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                            {seller.status === 'suspended' && <AlertTriangle className="h-3 w-3 mr-1" />}
                            {seller.status === 'pending' ? 'En attente' :
                             seller.status === 'approved' ? 'Approuvé' :
                             seller.status === 'rejected' ? 'Refusé' :
                             seller.status === 'suspended' ? 'Suspendu' : seller.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2 justify-end">
                            {seller.status === 'approved' && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedSeller(seller)
                                    setShowPremiumModal(true)
                                  }}
                                  className={seller.subscription_type === 'premium' ? 'text-yellow-600' : 'text-primary'}
                                >
                                  <Crown className="h-4 w-4 mr-2" />
                                  {seller.subscription_type === 'premium' ? 'Rétrograder' : 'Premium'}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedSeller(seller)
                                    setShowVerifyModal(true)
                                  }}
                                  className={seller.profiles?.is_verified ? 'text-blue-600' : 'text-primary'}
                                >
                                  <Shield className="h-4 w-4 mr-2" />
                                  {seller.profiles?.is_verified ? 'Dévérifier' : 'Vérifier'}
                                </Button>
                              </>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedSeller(seller)
                                setShowDetails(true)
                              }}
                            >
                              Détails
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}